'use client'

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Pencil, DollarSign, Navigation, Hand, Search, Camera, Download, Printer, Tag } from 'lucide-react'
import { useShell } from '../components/PageShell'
import ExportModal from '../components/ExportModal'
import BulkPrintDialog from '../components/print/BulkPrintDialog'
import BulkListedSlideout from '../components/BulkListedSlideout'

// Style constants from design system
const labelStyle = {
  color: '#E0E6F0',
  fontWeight: 500,
  fontSize: '0.875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  display: 'block',
  marginBottom: '8px'
}

const inputStyle = {
  backgroundColor: '#334155',
  border: '2px solid #8A95AA',
  borderRadius: '8px',
  color: '#E0E6F0',
  padding: '0.75rem 1rem',
  width: '100%',
  fontSize: '0.875rem',
  boxSizing: 'border-box' as const
}

interface Item {
  id: string
  ic: string
  sku: string | null
  title: string
  category_name: string
  location_name: string
  tote_code: string | null
  tote_name: string | null
  shelf_name: string | null
  tier_nickname: string | null
  status: string
  condition: string
  quantity: number
  micro_url: string | null
  thumbnail_url: string | null
  listings: Array<{ platform: string; price: string; status: string }> | null
}

interface SearchFilters {
  category_id: string
  location_id: string
  tote_id: string
  shelf_id: string
  status: string
  condition: string
  listed: string
  marketplaces: string[]
  intake_date_start: string
  intake_date_end: string
  sold_date_start: string
  sold_date_end: string
  processing_only: boolean
}

interface QuantityFilters {
  includeZeroQuantity: boolean
  zeroQuantityOnly: boolean
}

export default function SearchPage() {
  const { toast } = useShell()
  const navigate = useNavigate()

  // Read URL parameters immediately for initial state
  const getInitialFilters = (): SearchFilters => {
    const params = new URLSearchParams(window.location.search)
    const categoryId = params.get('category') || params.get('category_id')
    const toteId = params.get('tote_id')
    const shelfId = params.get('shelf_id')
    const locationId = params.get('location_id')

    return {
      category_id: categoryId || '',
      location_id: locationId || '',
      tote_id: toteId || '',
      shelf_id: shelfId || '',
      status: '',
      condition: '',
      listed: '',
      marketplaces: [],
      intake_date_start: '',
      intake_date_end: '',
      sold_date_start: '',
      sold_date_end: '',
      processing_only: false
    }
  }

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<SearchFilters>(getInitialFilters())
  const [quantityFilters, setQuantityFilters] = useState<QuantityFilters>({
    includeZeroQuantity: false,
    zeroQuantityOnly: false
  })

  // Sorting
  const [sortBy, setSortBy] = useState('ic')
  const [sortOrder, setSortOrder] = useState('desc')

  // UI State
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false)
  const [dateRangesOpen, setDateRangesOpen] = useState(false)
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false)
  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false)

  // Advanced Filters
  type FilterRule = {
    id: string
    field: string
    operator: string
    value: string
  }
  const [advancedFilters, setAdvancedFilters] = useState<FilterRule[]>([])

  // Saved Searches
  const [savedSearches, setSavedSearches] = useState<any[]>([])
  const [saveSearchName, setSaveSearchName] = useState('')

  // Pagination
  const [page, setPage] = useState(1)
  const limit = viewMode === 'table' ? 150 : 15

  // Results
  const [items, setItems] = useState<Item[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)

  // Selection
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [marketplaceSlideout, setMarketplaceSlideout] = useState(false)
  const [moveSlideout, setMoveSlideout] = useState(false)
  const [moveItems, setMoveItems] = useState<string[]>([])
  const [moveLocation, setMoveLocation] = useState('')
  const [moveTote, setMoveTote] = useState('')
  const [moveShelf, setMoveShelf] = useState('')
  const [moveTier, setMoveTier] = useState('')
  const [toteScanInput, setToteScanInput] = useState('')
  const [addItemInput, setAddItemInput] = useState('')

  // Filtered dropdowns for move slideout
  const [moveFilteredShelves, setMoveFilteredShelves] = useState<any[]>([])
  const [moveFilteredTiers, setMoveFilteredTiers] = useState<any[]>([])
  const [moveFilteredTotes, setMoveFilteredTotes] = useState<any[]>([])
  const [pickListSlideout, setPickListSlideout] = useState(false)
  const [pickListItems, setPickListItems] = useState<string[]>([])
  const [selectedPickList, setSelectedPickList] = useState('')
  const [createNewPickList, setCreateNewPickList] = useState(false)
  const [newPickListName, setNewPickListName] = useState('')
  const [addPickListItemInput, setAddPickListItemInput] = useState('')
  const [soldSlideout, setSoldSlideout] = useState(false)
  const [soldItems, setSoldItems] = useState<string[]>([])
  const [addSoldItemInput, setAddSoldItemInput] = useState('')
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [showBulkPrintDialog, setShowBulkPrintDialog] = useState(false)
  const [bulkEditSlideout, setBulkEditSlideout] = useState(false)
  const [bulkEditCategory, setBulkEditCategory] = useState('')
  const [bulkEditStatus, setBulkEditStatus] = useState('')
  const [bulkEditQuantity, setBulkEditQuantity] = useState('')
  const [bulkEditCondition, setBulkEditCondition] = useState('')
  const [bulkEditLocation, setBulkEditLocation] = useState('')
  const [bulkEditShelf, setBulkEditShelf] = useState('')
  const [bulkEditTier, setBulkEditTier] = useState('')
  const [bulkEditTote, setBulkEditTote] = useState('')

  // Dropdown Data
  const [categories, setCategories] = useState<any[]>([])
  const [locations, setLocations] = useState<any[]>([])
  const [totes, setTotes] = useState<any[]>([])
  const [shelves, setShelves] = useState<any[]>([])
  const [tiers, setTiers] = useState<any[]>([])
  const [pickLists, setPickLists] = useState<any[]>([])

  // Filtered dropdowns for bulk edit based on selections
  const [bulkEditFilteredShelves, setBulkEditFilteredShelves] = useState<any[]>([])
  const [bulkEditFilteredTiers, setBulkEditFilteredTiers] = useState<any[]>([])
  const [bulkEditFilteredTotes, setBulkEditFilteredTotes] = useState<any[]>([])

  // Hardcoded status and condition options (business_enums not populated)
  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'ready', label: 'Ready' },
    { value: 'reserved', label: 'Reserved' },
    { value: 'sold', label: 'Sold' }
  ]

  const conditions = [
    { value: 'new', label: 'New' },
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' }
  ]

  // Load dropdown data - reload locations when processing_only filter changes
  useEffect(() => {
    fetch('/api/categories-pg').then(r => r.json()).then(d => {
      if (d.success) setCategories(d.data)
    })
    const locationUrl = filters.processing_only ? '/api/locations-pg?is_processing=true' : '/api/locations-pg'
    fetch(locationUrl).then(r => r.json()).then(d => {
      if (d.success) setLocations(d.data)
    })
    fetch('/api/totes-pg').then(r => r.json()).then(d => {
      if (d.success) setTotes(d.data)
    })
    fetch('/api/shelves-pg').then(r => r.json()).then(d => {
      if (d.success) setShelves(d.data)
    })
    fetch('/api/tiers-pg').then(r => r.json()).then(d => {
      if (d.success) setTiers(d.data)
    })
    // Fetch active pick lists - refresh when slideout opens
    fetchPickLists()
    // Fetch saved searches
    fetchSavedSearches()
  }, [filters.processing_only])


  // Cascading dropdowns for bulk edit - when location changes, filter shelves and totes
  useEffect(() => {
    if (bulkEditLocation) {
      const filteredShelves = shelves.filter(s => s.location_id === bulkEditLocation)
      setBulkEditFilteredShelves(filteredShelves)
      const filteredTotes = totes.filter(t => t.location_id === bulkEditLocation)
      setBulkEditFilteredTotes(filteredTotes)

      // Reset dependent fields
      setBulkEditShelf('')
      setBulkEditTier('')
      setBulkEditTote('')
    } else {
      setBulkEditFilteredShelves([])
      setBulkEditFilteredTotes([])
      setBulkEditFilteredTiers([])
    }
  }, [bulkEditLocation, shelves, totes])

  // When shelf changes, filter tiers
  useEffect(() => {
    if (bulkEditShelf) {
      const filteredTiers = tiers.filter(t => t.shelf_id === bulkEditShelf)
      setBulkEditFilteredTiers(filteredTiers)

      // Reset tier selection
      setBulkEditTier('')
    } else {
      setBulkEditFilteredTiers([])
    }
  }, [bulkEditShelf, tiers])

  // Cascading dropdowns for move slideout - when location changes
  useEffect(() => {
    if (moveLocation) {
      const filteredShelves = shelves.filter(s => s.location_id === moveLocation)
      setMoveFilteredShelves(filteredShelves)
      const filteredTotes = totes.filter(t => t.location_id === moveLocation)
      setMoveFilteredTotes(filteredTotes)

      // Reset dependent fields
      setMoveShelf('')
      setMoveTier('')
      setMoveTote('')
    } else {
      setMoveFilteredShelves([])
      setMoveFilteredTotes([])
      setMoveFilteredTiers([])
    }
  }, [moveLocation, shelves, totes])

  // When move shelf changes, filter tiers
  useEffect(() => {
    if (moveShelf) {
      const filteredTiers = tiers.filter(t => t.shelf_id === moveShelf)
      setMoveFilteredTiers(filteredTiers)

      // Reset tier selection
      setMoveTier('')
    } else {
      setMoveFilteredTiers([])
    }
  }, [moveShelf, tiers])

  // Fetch items when filters/sort/page change
  const fetchItems = async () => {
    setLoading(true)
    const params = new URLSearchParams({
      q: searchQuery,
      sort_by: sortBy,
      sort_order: sortOrder,
      page: page.toString(),
      limit: limit.toString()
    })

    // Add filters
    if (filters.category_id) params.append('category_id', filters.category_id)
    if (filters.location_id) params.append('location_id', filters.location_id)
    if (filters.tote_id) params.append('tote_id', filters.tote_id)
    if (filters.shelf_id) params.append('shelf_id', filters.shelf_id)
    if (filters.status) params.append('status', filters.status)
    if (filters.condition) params.append('condition', filters.condition)
    if (filters.listed) params.append('listed', filters.listed)
    if (filters.intake_date_start) params.append('intake_date_start', filters.intake_date_start)
    if (filters.intake_date_end) params.append('intake_date_end', filters.intake_date_end)
    if (filters.sold_date_start) params.append('sold_date_start', filters.sold_date_start)
    if (filters.sold_date_end) params.append('sold_date_end', filters.sold_date_end)

    // Processing locations only filter
    if (filters.processing_only) {
      params.append('processing_only', 'true')
    }

    // Quantity filters
    if (quantityFilters.zeroQuantityOnly) {
      params.append('zeroQuantityOnly', 'true')
    } else if (quantityFilters.includeZeroQuantity) {
      params.append('includeZeroQuantity', 'true')
    }

    // Advanced filters
    if (advancedFilters.length > 0) {
      params.append('advancedFilters', JSON.stringify(advancedFilters))
    }

    try {
      const response = await fetch(`/api/search-items?${params}`)
      const data = await response.json()

      if (data.success) {
        // Deduplicate items by ID to prevent React key warnings
        const uniqueItems = Array.from(
          new Map(data.data.items.map((item: Item) => [item.id, item])).values()
        )
        setItems(uniqueItems)
        setTotalItems(data.data.total)
      }
    } catch (error) {
      console.error('Error fetching items:', error)
    } finally {
      setLoading(false)
    }
  }

  // Debounced search query
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchItems()
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Immediate fetch for filters, sort, and page changes
  useEffect(() => {
    fetchItems()
  }, [filters, quantityFilters, sortBy, sortOrder, page, viewMode, advancedFilters])

  // Selection handlers
  const toggleItemSelection = (itemId: string) => {
    const newSelection = new Set(selectedItems)
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId)
    } else {
      newSelection.add(itemId)
    }
    setSelectedItems(newSelection)
  }

  const selectAll = () => {
    const allIds = new Set(items.map(item => item.id))
    setSelectedItems(allIds)
  }

  const clearSelection = () => {
    setSelectedItems(new Set())
  }

  // Filter handlers
  const clearFilters = () => {
    setFilters({
      category_id: '',
      location_id: '',
      tote_id: '',
      shelf_id: '',
      status: '',
      condition: '',
      listed: '',
      marketplaces: [],
      intake_date_start: '',
      intake_date_end: '',
      sold_date_start: '',
      sold_date_end: '',
      processing_only: false
    })
    setSortBy('ic')
    setSortOrder('desc')
    setPage(1)
    // Don't clear quantityFilters - they persist
  }

  const handleSearch = () => {
    setPage(1)
    fetchItems()
  }

  // Move slideout functions
  const openMoveSlideout = (itemId: string) => {
    setMoveItems([itemId])
    setMoveSlideout(true)
  }

  const openBulkMoveSlideout = () => {
    setMoveItems(Array.from(selectedItems))
    setMoveSlideout(true)
  }

  const handleAddItemToMove = async () => {
    if (!addItemInput.trim()) return

    try {
      const response = await fetch(`/api/items-by-ic?ic=${addItemInput.trim()}`)
      const data = await response.json()

      if (data.success && data.data) {
        if (!moveItems.includes(data.data.id)) {
          setMoveItems([...moveItems, data.data.id])
          setAddItemInput('')
          toast({ title: 'Success', description: 'Item added to move list', type: 'success' })
        } else {
          toast({ title: 'Info', description: 'Item already in list', type: 'info' })
        }
      } else {
        toast({ title: 'Error', description: 'Item not found', type: 'error' })
      }
    } catch (error) {
      console.error('Error fetching item:', error)
      toast({ title: 'Error', description: 'Failed to fetch item', type: 'error' })
    }
  }

  const handleToteScan = (toteCode: string) => {
    const tote = totes.find(t =>
      t.code?.toLowerCase() === toteCode.toLowerCase() ||
      t.name?.toLowerCase() === toteCode.toLowerCase()
    )

    if (tote) {
      setMoveTote(tote.id)
      setMoveLocation(tote.location_id)
      toast({ title: 'Success', description: 'Tote scanned successfully', type: 'success' })
    } else {
      toast({ title: 'Error', description: 'Tote not found', type: 'error' })
    }
  }

  const handleMoveSubmit = async () => {
    if (!moveLocation) return

    try {
      const payload: any = {
        item_ids: moveItems,
        location_id: moveLocation
      }

      // Add storage location (can have both tote AND shelf)
      if (moveTote) {
        payload.tote_id = moveTote
      } else {
        // If no tote selected, explicitly clear it
        payload.tote_id = null
      }

      if (moveShelf) {
        payload.shelf_id = moveShelf
        if (moveTier) {
          payload.tier_id = moveTier
        }
      } else {
        // If no shelf selected, explicitly clear it and tier
        payload.shelf_id = null
        payload.tier_id = null
      }

      const response = await fetch('/api/bulk-move-items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (data.success) {
        toast({ title: 'Success', description: 'Items moved successfully', type: 'success' })
        setMoveSlideout(false)
        setMoveItems([])
        setMoveLocation('')
        setMoveTote('')
        setMoveShelf('')
        setMoveTier('')
        setToteScanInput('')
        setAddItemInput('')
        setSelectedItems(new Set())
        fetchItems()
      } else {
        toast({ title: 'Error', description: 'Error moving items', type: 'error' })
      }
    } catch (error) {
      console.error('Error moving items:', error)
      toast({ title: 'Error', description: 'Failed to move items', type: 'error' })
    }
  }

  const removeItemFromMove = (itemId: string) => {
    setMoveItems(moveItems.filter(id => id !== itemId))
  }

  // Fetch pick lists function
  const fetchPickLists = async () => {
    try {
      const response = await fetch('/api/picklists-pg?status=active')
      const data = await response.json()
      if (data.success) {
        setPickLists(data.data)
      }
    } catch (error) {
      console.error('Error fetching pick lists:', error)
      toast({ title: 'Error', description: 'Failed to fetch pick lists', type: 'error' })
    }
  }

  // Fetch saved searches
  const fetchSavedSearches = async () => {
    try {
      const response = await fetch('/api/saved-searches')
      const data = await response.json()
      if (data.success) {
        setSavedSearches(data.data || [])
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error)
      toast({ title: 'Error', description: 'Failed to fetch saved searches', type: 'error' })
    }
  }

  // Add advanced filter rule
  const addAdvancedFilter = () => {
    const newRule: FilterRule = {
      id: Date.now().toString(),
      field: 'category_id',
      operator: 'is',
      value: ''
    }
    setAdvancedFilters([...advancedFilters, newRule])
  }

  // Remove advanced filter rule
  const removeAdvancedFilter = (id: string) => {
    setAdvancedFilters(advancedFilters.filter(f => f.id !== id))
  }

  // Update advanced filter rule
  const updateAdvancedFilter = (id: string, field: keyof FilterRule, value: string) => {
    setAdvancedFilters(advancedFilters.map(f =>
      f.id === id ? { ...f, [field]: value } : f
    ))
  }

  // Apply advanced filters
  const applyAdvancedFilters = () => {
    // Advanced filters will be sent to backend
    setPage(1)
    fetchItems()
  }

  // Clear advanced filters
  const clearAdvancedFilters = () => {
    setAdvancedFilters([])
    setPage(1)
    fetchItems()
  }

  // Save current search
  const saveCurrentSearch = async () => {
    if (!saveSearchName.trim()) {
      toast({ title: 'Error', description: 'Please enter a name for this search', type: 'error' })
      return
    }

    try {
      const searchData = {
        name: saveSearchName,
        filters: {
          ...filters,
          searchQuery,
          sortBy,
          sortOrder,
          advancedFilters
        }
      }

      const response = await fetch('/api/saved-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchData)
      })

      const data = await response.json()
      if (data.success) {
        setSaveSearchName('')
        fetchSavedSearches()
        toast({ title: 'Success', description: 'Search saved successfully!', type: 'success' })
      }
    } catch (error) {
      console.error('Error saving search:', error)
      toast({ title: 'Error', description: 'Failed to save search', type: 'error' })
    }
  }

  // Load saved search
  const loadSavedSearch = (search: any) => {
    const savedFilters = search.filters
    setFilters(savedFilters)
    setSearchQuery(savedFilters.searchQuery || '')
    setSortBy(savedFilters.sortBy || 'ic')
    setSortOrder(savedFilters.sortOrder || 'desc')
    setAdvancedFilters(savedFilters.advancedFilters || [])
    setPage(1)
    fetchItems()
  }

  // Delete saved search
  const deleteSavedSearch = async (id: string) => {
    if (!confirm('Are you sure you want to delete this saved search?')) return

    try {
      const response = await fetch(`/api/saved-searches?id=${id}`, {
        method: 'DELETE'
      })

      const data = await response.json()
      if (data.success) {
        fetchSavedSearches()
      }
    } catch (error) {
      console.error('Error deleting search:', error)
      toast({ title: 'Error', description: 'Failed to delete search', type: 'error' })
    }
  }

  // Pick List slideout functions
  const openPickListSlideout = () => {
    setPickListItems(Array.from(selectedItems))
    fetchPickLists() // Refresh pick lists when opening
    setPickListSlideout(true)
  }

  const openSingleItemPickList = (itemId: string) => {
    setPickListItems([itemId])
    fetchPickLists() // Refresh pick lists when opening
    setPickListSlideout(true)
  }

  const handleAddItemToPickList = async () => {
    if (!addPickListItemInput.trim()) return

    try {
      const response = await fetch(`/api/items-by-ic?ic=${addPickListItemInput.trim()}`)
      const data = await response.json()

      if (data.success && data.data) {
        if (!pickListItems.includes(data.data.id)) {
          setPickListItems([...pickListItems, data.data.id])
          setAddPickListItemInput('')
          toast({ title: 'Item Added', description: `${data.data.ic} added to pick list`, type: 'success' })
        } else {
          toast({ title: 'Already Added', description: `${data.data.ic} is already in the list`, type: 'info' })
        }
      } else {
        toast({ title: 'Not Found', description: `Item ${addPickListItemInput.trim()} not found`, type: 'error' })
      }
    } catch (error) {
      console.error('Error fetching item:', error)
      toast({ title: 'Error', description: 'Failed to fetch item', type: 'error' })
    }
  }

  const removeItemFromPickList = (itemId: string) => {
    setPickListItems(pickListItems.filter(id => id !== itemId))
  }

  const handlePickListSubmit = async (startPicking: boolean) => {
    if (pickListItems.length === 0) {
      toast({ title: 'No Items', description: 'Please add items to the pick list', type: 'error' })
      return
    }

    try {
      let pickListId = selectedPickList

      // Create new pick list if needed
      if (createNewPickList) {
        if (!newPickListName.trim()) {
          toast({ title: 'Name Required', description: 'Please enter a pick list name', type: 'error' })
          return
        }

        const createResponse = await fetch('/api/picklists-pg', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newPickListName,
            item_ids: pickListItems
          })
        })

        const createData = await createResponse.json()

        if (createData.success) {
          pickListId = createData.data.id
          toast({
            title: 'Pick List Created',
            description: `${newPickListName} created with ${pickListItems.length} items`,
            type: 'success'
          })
        } else {
          toast({ title: 'Error', description: 'Failed to create pick list', type: 'error' })
          return
        }
      } else {
        // Add to existing pick list
        if (!pickListId) {
          toast({ title: 'Selection Required', description: 'Please select a pick list', type: 'error' })
          return
        }

        const addResponse = await fetch('/api/add-to-picklist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pick_list_id: pickListId,
            item_ids: pickListItems
          })
        })

        const addData = await addResponse.json()

        if (addData.success) {
          const addedCount = addData.added_count || 0
          const errorCount = addData.error_count || 0

          if (addedCount > 0) {
            toast({
              title: 'Items Added',
              description: `${addedCount} item(s) added to pick list${errorCount > 0 ? `, ${errorCount} skipped` : ''}`,
              type: 'success'
            })
          } else if (errorCount > 0) {
            toast({
              title: 'Items Skipped',
              description: `${errorCount} item(s) already in picklist or not found`,
              type: 'warning'
            })
          }
        } else {
          toast({ title: 'Error', description: addData.error || 'Failed to add items to pick list', type: 'error' })
          return
        }
      }

      // Navigate to picking interface if startPicking is true
      if (startPicking && pickListId) {
        window.location.href = `/picklists?id=${pickListId}&start=true`
      } else {
        // Reset state only if not navigating away
        setPickListSlideout(false)
        setPickListItems([])
        setSelectedPickList('')
        setCreateNewPickList(false)
        setNewPickListName('')
        setAddPickListItemInput('')
        setSelectedItems(new Set())
        fetchItems()
      }
    } catch (error) {
      console.error('Error with pick list:', error)
      toast({ title: 'Error', description: 'Failed to process pick list', type: 'error' })
    }
  }

  const handleCreateNewToggle = () => {
    setCreateNewPickList(!createNewPickList)
    if (!createNewPickList) {
      setSelectedPickList('')
      // Auto-generate name
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0]
      const count = pickLists.length + 1
      setNewPickListName(`Pick List ${dateStr}-${String(count).padStart(3, '0')}`)
    }
  }

  // Sold slideout functions
  const openSoldSlideout = () => {
    setSoldItems(Array.from(selectedItems))
    setSoldSlideout(true)
  }

  const openSingleItemSold = (itemId: string) => {
    setSoldItems([itemId])
    setSoldSlideout(true)
  }

  const handleAddItemToSold = async () => {
    if (!addSoldItemInput.trim()) return

    try {
      const response = await fetch(`/api/items-by-ic?ic=${addSoldItemInput.trim()}`)
      const data = await response.json()

      if (data.success && data.data) {
        if (!soldItems.includes(data.data.id)) {
          setSoldItems([...soldItems, data.data.id])
          setAddSoldItemInput('')
          toast({ title: 'Item Added', description: `${data.data.ic} added to sold list`, type: 'success' })
        } else {
          toast({ title: 'Already Added', description: `${data.data.ic} is already in the list`, type: 'info' })
        }
      } else {
        toast({ title: 'Not Found', description: `Item ${addSoldItemInput.trim()} not found`, type: 'error' })
      }
    } catch (error) {
      console.error('Error fetching item:', error)
      toast({ title: 'Error', description: 'Failed to fetch item', type: 'error' })
    }
  }

  const removeItemFromSold = (itemId: string) => {
    setSoldItems(soldItems.filter(id => id !== itemId))
  }

  const handleProceedToSaleEntry = () => {
    if (soldItems.length === 0) {
      toast({ title: 'No Items', description: 'Please add items to mark as sold', type: 'error' })
      return
    }

    const itemIds = soldItems.join(',')
    window.location.href = `/enter-sale?items=${itemIds}`
  }

  // Pagination - show up to 10 pages
  const totalPages = Math.ceil(totalItems / limit)
  const pageNumbers = []
  for (let i = 1; i <= Math.min(totalPages, 10); i++) {
    pageNumbers.push(i)
  }

  return (
    <div suppressHydrationWarning style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page Title */}
        <h1 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '3rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#FB923C',
          textShadow: '0 0 20px rgba(251, 146, 60, 0.8), 0 0 40px rgba(251, 146, 60, 0.4)',
          marginBottom: '32px'
        }}>
          SEARCH & FILTERS
        </h1>

        {/* Main Search Bar */}
        <div style={{
          background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(251, 146, 60, 0.05), rgba(17, 24, 39, 0.95))',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(251, 146, 60, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          position: 'relative',
          marginBottom: '24px',
          overflow: 'hidden',
          transition: 'all 0.3s'
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))', pointerEvents: 'none' }} />

          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', position: 'relative', zIndex: 10 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, IC number, or SKU..."
                style={{
                  ...inputStyle,
                  paddingRight: '48px',
                  height: '48px',
                  fontSize: '1rem'
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button style={{
                position: 'absolute',
                right: '4px',
                top: '4px',
                padding: '8px',
                borderRadius: '6px',
                background: '#242938',
                border: '2px solid #FF6B35',
                color: '#E0E6F0',
                cursor: 'pointer'
              }}>
                📷
              </button>
            </div>
            <button onClick={handleSearch} title="Search" style={{
              height: '48px',
              width: '48px',
              padding: '12px',
              background: 'linear-gradient(90deg, #D9440F, #FF6B35)',
              border: '1px solid #FF9B50',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 0 20px rgba(255, 107, 53, 0.6)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Search style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </div>

        {/* Filter Categories */}
        <div style={{
          background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(236, 72, 153, 0.05), rgba(17, 24, 39, 0.95))',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(236, 72, 153, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          position: 'relative',
          marginBottom: '24px',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(236, 72, 153, 0.1))', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 10 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={labelStyle}>Category</label>
                <select
                  value={filters.category_id}
                  onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <select
                  value={filters.location_id}
                  onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    id="processing_only"
                    checked={filters.processing_only}
                    onChange={(e) => setFilters({ ...filters, processing_only: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <label htmlFor="processing_only" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                    Processing Locations Only
                  </label>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Tote</label>
                <select
                  value={filters.tote_id}
                  onChange={(e) => setFilters({ ...filters, tote_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All Totes</option>
                  {totes.map(tote => (
                    <option key={tote.id} value={tote.id}>{tote.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Shelf</label>
                <select
                  value={filters.shelf_id}
                  onChange={(e) => setFilters({ ...filters, shelf_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All Shelves</option>
                  {shelves.map(shelf => (
                    <option key={shelf.id} value={shelf.id}>{shelf.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All Status</option>
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Condition</label>
                <select
                  value={filters.condition}
                  onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All Condition</option>
                  {conditions.map(cond => (
                    <option key={cond.value} value={cond.value}>{cond.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Listed</label>
                <select
                  value={filters.listed}
                  onChange={(e) => setFilters({ ...filters, listed: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">All</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Options (Collapsible) - Combines Date Ranges, Quantity Filters, Saved Searches, Advanced Filters */}
        <div style={{
          background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(168, 85, 247, 0.05), rgba(17, 24, 39, 0.95))',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          position: 'relative',
          marginBottom: '24px',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(168, 85, 247, 0.1))', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 10 }}>
            <div
              onClick={() => setAdvancedOptionsOpen(!advancedOptionsOpen)}
              style={{ cursor: 'pointer', marginBottom: advancedOptionsOpen ? '24px' : 0 }}
            >
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1.5rem',
                color: '#A78BFA',
                fontWeight: 700
              }}>
                {advancedOptionsOpen ? '▲' : '▼'} Advanced Options
              </h3>
            </div>
            {advancedOptionsOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                {/* Date Ranges */}
                <div style={{
                  background: 'rgba(6, 182, 212, 0.05)',
                  border: '1px solid rgba(6, 182, 212, 0.2)',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '1.1rem',
                    color: '#06B6D4',
                    marginBottom: '16px',
                    fontWeight: 600
                  }}>
                    📅 Date Ranges
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                <div>
                  <label style={{ ...labelStyle, textAlign: 'center' }}>Intake Date</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={filters.intake_date_start}
                      onChange={(e) => setFilters({ ...filters, intake_date_start: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <span style={{ color: '#8A95AA', flexShrink: 0 }}>to</span>
                    <input
                      type="date"
                      value={filters.intake_date_end}
                      onChange={(e) => setFilters({ ...filters, intake_date_end: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ ...labelStyle, textAlign: 'center' }}>Listed Date</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input type="date" disabled style={{ ...inputStyle, flex: 1, opacity: 0.5 }} />
                    <span style={{ color: '#8A95AA', flexShrink: 0 }}>to</span>
                    <input type="date" disabled style={{ ...inputStyle, flex: 1, opacity: 0.5 }} />
                  </div>
                  <small style={{ display: 'block', textAlign: 'center', marginTop: '4px', color: '#8A95AA' }}>Coming soon</small>
                </div>
                <div>
                  <label style={{ ...labelStyle, textAlign: 'center' }}>Sold Date</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={filters.sold_date_start}
                      onChange={(e) => setFilters({ ...filters, sold_date_start: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <span style={{ color: '#8A95AA', flexShrink: 0 }}>to</span>
                    <input
                      type="date"
                      value={filters.sold_date_end}
                      onChange={(e) => setFilters({ ...filters, sold_date_end: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                  </div>
                </div>
                  </div>
                </div>

                {/* Quantity Filters */}
                <div style={{
                  background: 'rgba(34, 197, 94, 0.05)',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '1.1rem',
                    color: '#22C55E',
                    marginBottom: '12px',
                    fontWeight: 600
                  }}>
                    🔢 Quantity Filters
                  </h4>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="includeZeroQuantity"
                  checked={quantityFilters.includeZeroQuantity}
                  onChange={(e) => setQuantityFilters({ includeZeroQuantity: e.target.checked, zeroQuantityOnly: false })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="includeZeroQuantity" style={{ color: '#E0E6F0', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500 }}>
                  Include Zero Quantity Items
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="zeroQuantityOnly"
                  checked={quantityFilters.zeroQuantityOnly}
                  onChange={(e) => setQuantityFilters({ includeZeroQuantity: false, zeroQuantityOnly: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="zeroQuantityOnly" style={{ color: '#E0E6F0', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500 }}>
                  Zero Quantity Only
                </label>
              </div>
            </div>
                </div>

                {/* Saved Searches */}
                <div style={{
                  background: 'rgba(147, 51, 234, 0.05)',
                  border: '1px solid rgba(147, 51, 234, 0.2)',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '1.1rem',
                    color: '#A78BFA',
                    marginBottom: '16px',
                    fontWeight: 600
                  }}>
                    ⭐ Saved Searches ({savedSearches.length})
                  </h4>
              <div>
                {/* Save Current Search */}
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(147, 51, 234, 0.1)', borderRadius: '8px' }}>
                  <div style={{ color: '#A78BFA', fontSize: '0.875rem', fontWeight: 600, marginBottom: '8px', fontFamily: 'Rajdhani, sans-serif', textTransform: 'uppercase' }}>
                    Save Current Search
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={saveSearchName}
                      onChange={(e) => setSaveSearchName(e.target.value)}
                      placeholder="Enter search name..."
                      style={{
                        flex: 1,
                        background: '#334155',
                        border: '2px solid #8A95AA',
                        borderRadius: '8px',
                        color: '#E0E6F0',
                        padding: '8px 12px',
                        fontSize: '0.875rem'
                      }}
                    />
                    <button
                      onClick={saveCurrentSearch}
                      style={{
                        padding: '8px 16px',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        borderRadius: '8px'
                      }}
                    >
                      💾 Save
                    </button>
                  </div>
                </div>

                {/* Saved Searches List */}
                {savedSearches.length === 0 ? (
                  <div style={{ color: '#8A95AA', fontSize: '0.875rem', textAlign: 'center', padding: '16px' }}>
                    No saved searches yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {savedSearches.map(search => (
                      <div key={search.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        background: 'rgba(147, 51, 234, 0.1)',
                        border: '1px solid rgba(147, 51, 234, 0.3)',
                        borderRadius: '8px'
                      }}>
                        <div
                          onClick={() => loadSavedSearch(search)}
                          style={{
                            flex: 1,
                            color: '#E0E6F0',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            fontFamily: 'Rajdhani, sans-serif'
                          }}
                        >
                          ⭐ {search.name}
                        </div>
                        <button
                          onClick={() => deleteSavedSearch(search.id)}
                          style={{
                            padding: '4px 12px',
                            background: '#EF4444',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
                </div>

                {/* Advanced Filters */}
                <div style={{
                  background: 'rgba(239, 68, 68, 0.05)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '1.1rem',
                    color: '#EF4444',
                    marginBottom: '16px',
                    fontWeight: 600
                  }}>
                    🔧 Advanced Filters {advancedFilters.length > 0 && `(${advancedFilters.length} active)`}
                  </h4>
              <div>
                {/* Filter Rules */}
                {advancedFilters.map(filter => (
                  <div key={filter.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr auto',
                    gap: '8px',
                    marginBottom: '12px',
                    padding: '12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '8px'
                  }}>
                    {/* Field */}
                    <select
                      value={filter.field}
                      onChange={(e) => updateAdvancedFilter(filter.id, 'field', e.target.value)}
                      style={{
                        background: '#334155',
                        border: '2px solid #8A95AA',
                        borderRadius: '8px',
                        color: '#E0E6F0',
                        padding: '8px 12px',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="category_id">Category</option>
                      <option value="location_id">Location</option>
                      <option value="status">Status</option>
                      <option value="condition">Condition</option>
                      <option value="tote_id">Tote</option>
                      <option value="shelf_id">Shelf</option>
                    </select>

                    {/* Operator */}
                    <select
                      value={filter.operator}
                      onChange={(e) => updateAdvancedFilter(filter.id, 'operator', e.target.value)}
                      style={{
                        background: '#334155',
                        border: '2px solid #8A95AA',
                        borderRadius: '8px',
                        color: '#E0E6F0',
                        padding: '8px 12px',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="is">IS</option>
                      <option value="is_not">IS NOT</option>
                      <option value="contains">CONTAINS</option>
                      <option value="not_contains">NOT CONTAINS</option>
                    </select>

                    {/* Value */}
                    {filter.field === 'category_id' ? (
                      <select
                        value={filter.value}
                        onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                        style={{
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '8px 12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <option value="">Select...</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    ) : filter.field === 'location_id' ? (
                      <select
                        value={filter.value}
                        onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                        style={{
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '8px 12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <option value="">Select...</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    ) : filter.field === 'status' ? (
                      <select
                        value={filter.value}
                        onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                        style={{
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '8px 12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <option value="">Select...</option>
                        {statuses.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    ) : filter.field === 'condition' ? (
                      <select
                        value={filter.value}
                        onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                        style={{
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '8px 12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <option value="">Select...</option>
                        {conditions.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={filter.value}
                        onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                        placeholder="Value..."
                        style={{
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '8px 12px',
                          fontSize: '0.875rem'
                        }}
                      />
                    )}

                    {/* Remove button */}
                    <button
                      onClick={() => removeAdvancedFilter(filter.id)}
                      style={{
                        padding: '8px 12px',
                        background: '#EF4444',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {/* Add Rule Button */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button
                    onClick={addAdvancedFilter}
                    style={{
                      flex: 1,
                      padding: '12px',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      borderRadius: '8px'
                    }}
                  >
                    + Add Rule
                  </button>
                  {advancedFilters.length > 0 && (
                    <button
                      onClick={clearAdvancedFilters}
                      style={{
                        padding: '12px 20px',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        background: '#242938',
                        border: '2px solid #8A95AA',
                        color: '#8A95AA',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        borderRadius: '8px'
                      }}
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Sorting and View Controls */}
        <div style={{
          background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(168, 85, 247, 0.05), rgba(17, 24, 39, 0.95))',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          position: 'relative',
          marginBottom: '24px',
          overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(168, 85, 247, 0.1))', pointerEvents: 'none' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', position: 'relative', zIndex: 10 }}>
            <button onClick={clearFilters} style={{
              padding: '12px 20px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              background: '#242938',
              border: '2px solid #8A95AA',
              color: '#8A95AA',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}>
              ✕ Clear Filters
            </button>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{ ...inputStyle, width: 'auto' }}
                >
                  <option value="ic">Item Code (IC)</option>
                  <option value="name">Name</option>
                  <option value="created_at">Intake Date (Created)</option>
                  <option value="acquisition_date">Acquisition Date</option>
                  <option value="sale_date">Sold Date</option>
                  <option value="cost">Price</option>
                  <option value="quantity">Quantity</option>
                  <option value="location">Location</option>
                  <option value="sku">SKU</option>
                  <option value="category">Category</option>
                  <option value="condition">Condition</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  style={{ ...inputStyle, width: 'auto' }}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ fontSize: '1rem', color: '#8A95AA' }}>
              <strong>{loading ? 'Loading...' : `${totalItems} items found`}</strong>
              {!loading && totalItems >= 150 && (
                <div style={{ color: '#FB923C', fontSize: '0.875rem', marginTop: '4px' }}>
                  ⚠️ Showing first 150 items. Please refine your search to see more results.
                </div>
              )}
            </div>
            <button onClick={() => window.location.href = '/picklists'} style={{
              padding: '12px 20px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #00F0FF 0%, #0891b2 100%)',
              border: '2px solid #00F0FF',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              boxShadow: '0 4px 6px rgba(0, 240, 255, 0.3)'
            }}>
              📋 Open Pick Lists
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setViewMode('cards')} style={{
              padding: '12px 20px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              background: viewMode === 'cards' ? 'linear-gradient(90deg, #D9440F, #FF6B35)' : '#242938',
              border: viewMode === 'cards' ? '1px solid #FF9B50' : '2px solid #FF6B35',
              color: viewMode === 'cards' ? 'white' : '#E0E6F0',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}>
              📦 Cards
            </button>
            <button onClick={() => setViewMode('table')} style={{
              padding: '12px 20px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 700,
              textTransform: 'uppercase',
              background: viewMode === 'table' ? 'linear-gradient(90deg, #D9440F, #FF6B35)' : '#242938',
              border: viewMode === 'table' ? '1px solid #FF9B50' : '2px solid #FF6B35',
              color: viewMode === 'table' ? 'white' : '#E0E6F0',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}>
              📊 Table
            </button>
            <button
              onClick={() => navigate('/qr-scanner')}
              style={{
                padding: '12px 20px',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 700,
                textTransform: 'uppercase',
                background: '#242938',
                border: '2px solid #FF6B35',
                color: '#E0E6F0',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}>
              🔍 Scanner
            </button>
          </div>
        </div>

        {/* Bulk Actions (shown when items selected) */}
        {selectedItems.size > 0 && (
          <div style={{
            background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(6, 182, 212, 0.05), rgba(17, 24, 39, 0.95))',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            borderRadius: '12px',
            padding: '24px',
            position: 'relative',
            marginBottom: '24px',
            overflow: 'hidden',
            boxShadow: '0 0 30px rgba(6, 182, 212, 0.2)'
          }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(6, 182, 212, 0.1))', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '1.25rem',
                  color: '#00F0FF'
                }}>
                  ☑ Bulk Actions - {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
                </h3>
                <button onClick={selectAll} style={{
                  padding: '6px 12px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #FF6B35',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}>
                  ☑ Select All
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => setBulkEditSlideout(true)} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #A78BFA',
                  color: '#A78BFA',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  ✏️ Bulk Edit
                </button>
                <button onClick={openBulkMoveSlideout} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #FF6B35',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  🧭 Move
                </button>
                <button onClick={() => setMarketplaceSlideout(true)} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #FF6B35',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  🏷️ Mark Listed
                </button>
                <button onClick={openPickListSlideout} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #00F0FF',
                  color: '#00F0FF',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  🚀 Add to Pick List
                </button>
                <button onClick={openSoldSlideout} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #FF6B35',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  💰 Mark Sold
                </button>
                <button onClick={() => setShowBulkPrintDialog(true)} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #00F0FF',
                  color: '#00F0FF',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Printer size={18} />
                  Print Labels
                </button>
                <button style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #FF6B35',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onClick={() => setExportModalOpen(true)}>
                  <Download style={{ width: 18, height: 18 }} />
                  Export
                </button>
                <button onClick={clearSelection} style={{
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #8A95AA',
                  color: '#8A95AA',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}>
                  ✕ Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Item Cards Grid or Table */}
        {viewMode === 'cards' ? (
          <div
            data-testid="search-results-grid"
            style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {items.map((item) => (
            <div
              key={item.id}
              data-testid={`item-card-${item.ic}`}
              style={{
                background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(59, 130, 246, 0.2), rgba(17, 24, 39, 0.95))',
                backdropFilter: 'blur(12px)',
                border: selectedItems.has(item.id) ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                position: 'relative',
                transition: 'all 0.3s',
                overflow: 'hidden',
                boxShadow: selectedItems.has(item.id) ? '0 0 30px rgba(251, 146, 60, 0.2)' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)'
                e.currentTarget.style.transform = 'scale(1.01)'
                e.currentTarget.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.2)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = selectedItems.has(item.id) ? 'rgba(251, 146, 60, 0.4)' : 'rgba(59, 130, 246, 0.2)'
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.boxShadow = selectedItems.has(item.id) ? '0 0 30px rgba(251, 146, 60, 0.2)' : 'none'
              }}
            >
              {/* Corner Accent */}
              <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(59, 130, 246, 0.1))', pointerEvents: 'none' }} />

              {/* Checkbox */}
              <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 20 }}>
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.id)}
                  onChange={() => toggleItemSelection(item.id)}
                  style={{ width: '40px', height: '40px', cursor: 'pointer' }}
                />
              </div>

              {/* Image */}
              <div
                onClick={() => navigate(`/items/${item.id}`)}
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  background: 'rgba(51, 65, 85, 0.5)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  position: 'relative',
                  zIndex: 10,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                {item.thumbnail_url ? (
                  <img src={item.thumbnail_url} alt={item.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: '#8A95AA' }}>📷 No Image</span>
                )}
                {/* Status Badge */}
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderRadius: '4px',
                  background: item.status === 'active' ? 'rgba(34, 197, 94, 0.3)' : item.status === 'ready' ? 'rgba(59, 130, 246, 0.3)' : item.status === 'sold' ? 'rgba(239, 68, 68, 0.3)' : item.status === 'reserved' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(148, 163, 184, 0.3)',
                  color: item.status === 'active' ? '#22C55E' : item.status === 'ready' ? '#3B82F6' : item.status === 'sold' ? '#EF4444' : item.status === 'reserved' ? '#EAB308' : '#94A3B8',
                  border: `1px solid ${item.status === 'active' ? '#22C55E' : item.status === 'ready' ? '#3B82F6' : item.status === 'sold' ? '#EF4444' : item.status === 'reserved' ? '#EAB308' : '#94A3B8'}`
                }}>
                  {item.status}
                </span>
                {/* Listings Overlay - Bottom Center */}
                {item.listings && item.listings.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '4px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    maxWidth: '90%'
                  }}>
                    {item.listings.map((listing, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderRadius: '4px',
                          background: 'rgba(251, 146, 60, 0.9)',
                          color: '#000',
                          border: '1px solid #FB923C',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Tag style={{ width: 10, height: 10 }} />
                        {listing.platform}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1.125rem',
                fontWeight: 600,
                color: 'rgb(191, 219, 254)',
                marginBottom: '12px',
                position: 'relative',
                zIndex: 10
              }}>
                {item.title}
              </div>

              {/* IC & SKU */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '8px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>IC</span>
                <span style={{ color: 'rgb(251, 146, 60)', fontFamily: 'monospace', fontWeight: 500 }}>{item.ic}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '8px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>SKU</span>
                <span style={{ color: 'white', fontWeight: 500 }}>{item.sku || 'N/A'}</span>
              </div>

              {/* Location & Tote */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '8px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>Location</span>
                <span style={{ color: 'rgb(147, 197, 253)' }}>{item.location_name}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '8px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>Storage</span>
                <span style={{ color: 'white', fontWeight: 500 }}>
                  {item.tote_name && <span>Tote: {item.tote_name}</span>}
                  {item.tote_name && (item.shelf_name || item.tier_nickname) && <span> | </span>}
                  {item.shelf_name && <span>Shelf: {item.shelf_name}</span>}
                  {item.shelf_name && item.tier_nickname && <span> / </span>}
                  {item.tier_nickname && <span>Tier: {item.tier_nickname}</span>}
                  {!item.tote_name && !item.shelf_name && !item.tier_nickname && <span>N/A</span>}
                </span>
              </div>

              {/* Category */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '8px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>Category</span>
                <span style={{ color: 'white', fontWeight: 500 }}>{item.category_name}</span>
              </div>

              {/* Qty & Condition */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '8px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>Qty</span>
                <span style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{item.quantity}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '12px', position: 'relative', zIndex: 10 }}>
                <span style={{ color: 'rgb(156, 163, 175)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>Condition</span>
                <span style={{ color: 'white', fontWeight: 500 }}>{item.condition}</span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '6px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(59, 130, 246, 0.2)', position: 'relative', zIndex: 10 }}>
                <button onClick={() => navigate(`/items/${item.id}`)} style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '6px',
                  color: 'rgb(147, 197, 253)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }} title="View" onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)' }}>
                  <Eye style={{ width: 21, height: 21 }} />
                </button>
                <button onClick={() => navigate(`/items/${item.id}?edit=true`)} style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(251, 146, 60, 0.1)',
                  border: '1px solid rgba(251, 146, 60, 0.3)',
                  borderRadius: '6px',
                  color: 'rgb(251, 146, 60)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }} title="Edit" onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251, 146, 60, 0.2)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(251, 146, 60, 0.1)' }}>
                  <Pencil style={{ width: 21, height: 21 }} />
                </button>
                <button onClick={() => openMoveSlideout(item.id)} style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '6px',
                  color: 'rgb(192, 132, 252)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }} title="Move" onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(168, 85, 247, 0.2)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)' }}>
                  <Navigation style={{ width: 21, height: 21 }} />
                </button>
                <button onClick={() => openSingleItemPickList(item.id)} style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '6px',
                  color: 'rgb(34, 211, 238)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }} title="Add to Pick List" onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(6, 182, 212, 0.2)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(6, 182, 212, 0.1)' }}>
                  <Hand style={{ width: 21, height: 21 }} />
                </button>
                <button onClick={() => openSingleItemSold(item.id)} style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '6px',
                  color: 'rgb(74, 222, 128)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }} title="Mark as Sold" onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34, 197, 94, 0.2)' }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)' }}>
                  <DollarSign style={{ width: 21, height: 21 }} />
                </button>
              </div>
            </div>
          ))}
        </div>
        ) : (
          /* Table View */
          <div data-testid="search-results-table" style={{ marginBottom: '24px', overflowX: 'auto', background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.95), rgba(59, 130, 246, 0.05), rgba(17, 24, 39, 0.95))', backdropFilter: 'blur(12px)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '12px', padding: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: 'rgba(59, 130, 246, 0.1)', borderBottom: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase', width: '50px' }}>
                    <input type="checkbox" checked={selectedItems.size === items.length && items.length > 0} onChange={() => selectedItems.size === items.length ? setSelectedItems(new Set()) : setSelectedItems(new Set(items.map(i => i.id)))} style={{ width: '27px', height: '27px', cursor: 'pointer' }} />
                  </th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>Image</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>IC</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>SKU</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>Title</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>Location</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#FF9B50', fontFamily: 'Rajdhani, sans-serif', fontSize: '0.875rem', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid rgba(59, 130, 246, 0.1)', background: selectedItems.has(item.id) ? 'rgba(251, 146, 60, 0.1)' : 'transparent', transition: 'all 0.2s' }}>
                    <td style={{ padding: '12px' }}>
                      <input type="checkbox" checked={selectedItems.has(item.id)} onChange={() => toggleItemSelection(item.id)} style={{ width: '27px', height: '27px', cursor: 'pointer' }} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      {(item.micro_url || item.thumbnail_url) ? (
                        <img
                          src={item.micro_url || item.thumbnail_url}
                          alt={item.title}
                          loading="lazy"
                          onClick={() => navigate(`/items/${item.id}`)}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)'
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                            e.currentTarget.style.boxShadow = 'none'
                          }}
                        />
                      ) : (
                        <div style={{ width: '60px', height: '60px', background: '#334155', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#8A95AA' }}>No Image</div>
                      )}
                    </td>
                    <td style={{ padding: '12px', color: '#FFFFFF', fontWeight: 700 }}>{item.ic}</td>
                    <td style={{ padding: '12px', color: '#E0E6F0' }}>{item.sku || 'N/A'}</td>
                    <td style={{ padding: '12px', color: '#FF9B50', fontWeight: 600 }}>{item.title}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        borderRadius: '4px',
                        background: item.status === 'active' ? 'rgba(34, 197, 94, 0.3)' : item.status === 'ready' ? 'rgba(59, 130, 246, 0.3)' : item.status === 'sold' ? 'rgba(239, 68, 68, 0.3)' : item.status === 'reserved' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(148, 163, 184, 0.3)',
                        color: item.status === 'active' ? '#22C55E' : item.status === 'ready' ? '#3B82F6' : item.status === 'sold' ? '#EF4444' : item.status === 'reserved' ? '#EAB308' : '#94A3B8',
                        border: `1px solid ${item.status === 'active' ? '#22C55E' : item.status === 'ready' ? '#3B82F6' : item.status === 'sold' ? '#EF4444' : item.status === 'reserved' ? '#EAB308' : '#94A3B8'}`
                      }}>
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: '#E0E6F0' }}>{item.location_name} | <span style={{ color: '#FFFFFF', fontWeight: 600 }}>Tote {item.tote_code || 'N/A'}</span></td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => navigate(`/items/${item.id}`)} style={{ padding: '6px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '4px', color: 'rgb(147, 197, 253)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} title="View"><Eye style={{ width: 18, height: 18 }} /></button>
                        <button onClick={() => navigate(`/items/${item.id}?edit=true`)} style={{ padding: '6px', background: 'rgba(251, 146, 60, 0.1)', border: '1px solid rgba(251, 146, 60, 0.3)', borderRadius: '4px', color: 'rgb(251, 146, 60)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} title="Edit"><Pencil style={{ width: 18, height: 18 }} /></button>
                        <button onClick={() => openMoveSlideout(item.id)} style={{ padding: '6px', background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: '4px', color: 'rgb(192, 132, 252)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} title="Move"><Navigation style={{ width: 18, height: 18 }} /></button>
                        <button onClick={() => openSingleItemPickList(item.id)} style={{ padding: '6px', background: 'rgba(6, 182, 212, 0.1)', border: '1px solid rgba(6, 182, 212, 0.3)', borderRadius: '4px', color: 'rgb(34, 211, 238)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} title="Pick List"><Hand style={{ width: 18, height: 18 }} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination - only show for card view */}
        {viewMode === 'cards' && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              style={{
                minWidth: '40px',
                padding: '12px 20px',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 600,
                textTransform: 'uppercase',
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                color: 'rgb(192, 132, 252)',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                opacity: page === 1 ? 0.5 : 1,
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              ← Previous
            </button>
            {pageNumbers.map(num => (
              <button
                key={num}
                onClick={() => setPage(num)}
                style={{
                  minWidth: '40px',
                  padding: '12px 20px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  background: page === num ? 'rgba(251, 146, 60, 0.2)' : 'rgba(168, 85, 247, 0.1)',
                  border: page === num ? '1px solid rgba(251, 146, 60, 0.4)' : '1px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '8px',
                  color: page === num ? 'rgb(251, 146, 60)' : 'rgb(192, 132, 252)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '0.875rem'
                }}
              >
                {num}
              </button>
            ))}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              style={{
                minWidth: '40px',
                padding: '12px 20px',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 600,
                textTransform: 'uppercase',
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                color: 'rgb(192, 132, 252)',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                opacity: page === totalPages ? 0.5 : 1,
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              Next →
            </button>
          </div>
        )}

        <div style={{ height: '40px' }}></div>
      </div>

      {/* Bulk Listings Slideout */}
      <BulkListedSlideout
        isOpen={marketplaceSlideout}
        onClose={() => setMarketplaceSlideout(false)}
        itemIds={Array.from(selectedItems)}
        onSuccess={() => {
          fetchItems()
          setSelectedItems(new Set())
        }}
      />

      {/* Move Slideout */}
      {moveSlideout && (
        <>
          {/* Overlay */}
          <div onClick={() => setMoveSlideout(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000
          }} />

          {/* Slideout Panel */}
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '500px',
            maxWidth: '90vw',
            background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.98), rgba(168, 85, 247, 0.1), rgba(17, 24, 39, 0.98))',
            backdropFilter: 'blur(12px)',
            borderLeft: '1px solid rgba(168, 85, 247, 0.3)',
            boxShadow: '0 0 40px rgba(168, 85, 247, 0.3)',
            zIndex: 1001,
            padding: '32px',
            overflowY: 'auto'
          }}>
            <div style={{ position: 'relative' }}>
              {/* Corner Accent */}
              <div style={{ position: 'absolute', top: -32, right: -32, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(168, 85, 247, 0.2))', pointerEvents: 'none' }} />

              {/* Header */}
              <h2 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                background: 'linear-gradient(90deg, #FF6B35, #FF9B50, #00F0FF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '24px',
                position: 'relative',
                zIndex: 10
              }}>
                🧭 Move Items
              </h2>

              {/* Items to Move Section */}
              <div style={{
                background: 'rgba(0, 240, 255, 0.05)',
                border: '2px solid #00F0FF',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                position: 'relative',
                zIndex: 10
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: '#00F0FF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Items to Move</div>
                  <div style={{
                    background: '#00F0FF',
                    color: '#0F1419',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>{moveItems.length}</div>
                </div>

                {moveItems.map(itemId => {
                  const item = items.find(i => i.id === itemId)
                  if (!item) return null
                  return (
                    <div key={itemId} style={{
                      background: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      transition: 'all 0.2s'
                    }}>
                      <button onClick={() => removeItemFromMove(itemId)} style={{
                        float: 'right',
                        background: 'transparent',
                        border: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: '1.25rem',
                        padding: '4px',
                        lineHeight: 1
                      }}>×</button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ color: '#22C55E', fontSize: '1.25rem' }}>✓</span>
                        <span style={{ fontWeight: 700, color: '#FFFFFF', fontSize: '1rem', fontFamily: 'Rajdhani, sans-serif' }}>{item.ic}</span>
                        <span>-</span>
                        <span style={{ color: '#FF9B50', fontWeight: 600, fontFamily: 'Rajdhani, sans-serif' }}>{item.title}</span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#8A95AA', marginLeft: '28px', fontFamily: 'Rajdhani, sans-serif' }}>
                        Current: {item.location_name} / {item.tote_code || 'No tote'}
                      </div>
                    </div>
                  )
                })}

                {/* Add More Section */}
                <div style={{
                  background: 'rgba(255, 107, 53, 0.1)',
                  border: '2px dashed #FF6B35',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '16px'
                }}>
                  <label style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#FF9B50',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '8px',
                    display: 'block',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>🔍 Add More Items</label>
                  <div style={{ fontSize: '0.75rem', color: '#8A95AA', marginBottom: '8px', fontFamily: 'Rajdhani, sans-serif' }}>
                    Scan or enter IC number
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={addItemInput}
                      onChange={(e) => setAddItemInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddItemToMove()}
                      placeholder="IC12, IC13..."
                      style={{
                        flex: 1,
                        background: '#334155',
                        border: '2px solid #8A95AA',
                        borderRadius: '8px',
                        color: '#E0E6F0',
                        padding: '12px',
                        fontSize: '0.875rem',
                        fontFamily: 'Rajdhani, sans-serif'
                      }}
                    />
                    <button onClick={handleAddItemToMove} style={{
                      background: 'linear-gradient(90deg, #D9440F, #FF6B35)',
                      border: '1px solid #FF9B50',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '0 20px',
                      borderRadius: '8px',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      boxShadow: '0 0 15px rgba(255, 107, 53, 0.4)',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}>+</button>
                  </div>
                </div>
              </div>

              {/* Destination Section */}
              <div style={{ position: 'relative', zIndex: 10, marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: '#FF9B50',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '16px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}>Move To:</h3>

                {/* Tote Scan */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    color: '#E0E6F0',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Scan Tote (Auto-fills both)</label>
                  <input
                    type="text"
                    value={toteScanInput}
                    onChange={(e) => setToteScanInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleToteScan(e.currentTarget.value)
                        setToteScanInput('')
                      }
                    }}
                    placeholder="📷 Scan tote barcode..."
                    style={{
                      width: '100%',
                      background: '#334155',
                      border: '2px solid #00F0FF',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '12px 16px',
                      fontSize: '0.875rem',
                      fontFamily: 'Rajdhani, sans-serif',
                      boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)'
                    }}
                  />
                </div>

                <div style={{
                  textAlign: 'center',
                  margin: '24px 0',
                  color: '#8A95AA',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontFamily: 'Rajdhani, sans-serif'
                }}>Or select manually:</div>

                {/* Location */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    color: '#E0E6F0',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Location *</label>
                  <select
                    value={moveLocation}
                    onChange={(e) => setMoveLocation(e.target.value)}
                    style={{
                      width: '100%',
                      background: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '12px 16px',
                      fontSize: '0.875rem',
                      fontFamily: 'Rajdhani, sans-serif',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select location...</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                {/* Storage Options - Show after location selected */}
                {moveLocation && (
                  <>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#FF9B50',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      marginBottom: '12px',
                      fontFamily: 'Rajdhani, sans-serif'
                    }}>
                      Choose Storage Type:
                    </div>

                    {/* Tote */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        color: '#E0E6F0',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '8px',
                        fontFamily: 'Rajdhani, sans-serif'
                      }}>Tote <span style={{ color: '#8A95AA', fontSize: '0.75rem' }}>(optional)</span></label>
                      <select
                        value={moveTote}
                        onChange={(e) => setMoveTote(e.target.value)}
                        style={{
                          width: '100%',
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                          fontFamily: 'Rajdhani, sans-serif',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select tote...</option>
                        {moveFilteredTotes.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{
                      textAlign: 'center',
                      margin: '16px 0',
                      color: '#8A95AA',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      fontFamily: 'Rajdhani, sans-serif'
                    }}>— AND/OR —</div>

                    {/* Shelf */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        color: '#E0E6F0',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '8px',
                        fontFamily: 'Rajdhani, sans-serif'
                      }}>Shelf <span style={{ color: '#8A95AA', fontSize: '0.75rem' }}>(optional)</span></label>
                      <select
                        value={moveShelf}
                        onChange={(e) => setMoveShelf(e.target.value)}
                        style={{
                          width: '100%',
                          background: '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: '#E0E6F0',
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                          fontFamily: 'Rajdhani, sans-serif',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select shelf...</option>
                        {moveFilteredShelves.map(shelf => (
                          <option key={shelf.id} value={shelf.id}>{shelf.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Tier */}
                    {moveShelf && moveFilteredTiers.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{
                          display: 'block',
                          color: '#E0E6F0',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '8px',
                          fontFamily: 'Rajdhani, sans-serif'
                        }}>Tier <span style={{ color: '#8A95AA', fontSize: '0.75rem' }}>(optional)</span></label>
                        <select
                          value={moveTier}
                          onChange={(e) => setMoveTier(e.target.value)}
                          style={{
                            width: '100%',
                            background: '#334155',
                            border: '2px solid #8A95AA',
                            borderRadius: '8px',
                            color: '#E0E6F0',
                            padding: '12px 16px',
                            fontSize: '0.875rem',
                            fontFamily: 'Rajdhani, sans-serif',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select tier...</option>
                          {moveFilteredTiers.map(tier => (
                            <option key={tier.id} value={tier.id}>
                              {tier.nickname || `Tier ${tier.position}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 10 }}>
                <button onClick={handleMoveSubmit} disabled={!moveLocation} style={{
                  flex: 1,
                  padding: '16px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: moveLocation ? 'linear-gradient(90deg, #D9440F, #FF6B35)' : '#242938',
                  border: '1px solid #FF9B50',
                  color: 'white',
                  cursor: moveLocation ? 'pointer' : 'not-allowed',
                  opacity: moveLocation ? 1 : 0.5,
                  fontSize: '1rem',
                  borderRadius: '8px',
                  boxShadow: moveLocation ? '0 0 20px rgba(255, 107, 53, 0.6)' : 'none'
                }}>
                  Save ({moveItems.length})
                </button>
                <button onClick={() => setMoveSlideout(false)} style={{
                  padding: '16px 24px',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: '#242938',
                  border: '2px solid #8A95AA',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  borderRadius: '8px'
                }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bulk Edit Slideout */}
      {bulkEditSlideout && (
        <>
          {/* Overlay */}
          <div onClick={() => setBulkEditSlideout(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1000
          }} />

          {/* Slideout Panel */}
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '500px',
            maxWidth: '90vw',
            background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.98), rgba(167, 139, 250, 0.1), rgba(17, 24, 39, 0.98))',
            backdropFilter: 'blur(12px)',
            borderLeft: '1px solid rgba(167, 139, 250, 0.3)',
            boxShadow: '0 0 40px rgba(167, 139, 250, 0.3)',
            zIndex: 1001,
            padding: '32px',
            overflowY: 'auto'
          }}>
            <div style={{ position: 'relative' }}>
              {/* Corner Accent */}
              <div style={{ position: 'absolute', top: -32, right: -32, width: '128px', height: '128px', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(167, 139, 250, 0.2))', pointerEvents: 'none' }} />

              {/* Header */}
              <h2 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '1.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                background: 'linear-gradient(90deg, #A78BFA, #C4B5FD)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '24px',
                position: 'relative',
                zIndex: 10
              }}>
                ✏️ Bulk Edit Items
              </h2>

              {/* Selected Items Count */}
              <div style={{
                background: 'rgba(167, 139, 250, 0.1)',
                border: '2px solid #A78BFA',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                position: 'relative',
                zIndex: 10
              }}>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#A78BFA',
                  fontFamily: 'Rajdhani, sans-serif'
                }}>
                  Editing {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#8A95AA', marginTop: '4px' }}>
                  Only filled fields will be updated
                </div>
              </div>

              {/* Edit Fields */}
              <div style={{ position: 'relative', zIndex: 10 }}>
                {/* Category */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    color: '#E0E6F0',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Category</label>
                  <select
                    value={bulkEditCategory}
                    onChange={(e) => setBulkEditCategory(e.target.value)}
                    style={{
                      backgroundColor: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="">Don't change</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    color: '#E0E6F0',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Status</label>
                  <select
                    value={bulkEditStatus}
                    onChange={(e) => setBulkEditStatus(e.target.value)}
                    style={{
                      backgroundColor: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="">Don't change</option>
                    {statuses.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Condition */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    color: '#E0E6F0',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Condition</label>
                  <select
                    value={bulkEditCondition}
                    onChange={(e) => setBulkEditCondition(e.target.value)}
                    style={{
                      backgroundColor: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="">Don't change</option>
                    {conditions.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {/* Quantity */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    color: '#E0E6F0',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Quantity</label>
                  <input
                    type="number"
                    value={bulkEditQuantity}
                    onChange={(e) => setBulkEditQuantity(e.target.value)}
                    placeholder="Leave empty to keep current"
                    style={{
                      backgroundColor: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                {/* Divider */}
                <div style={{
                  borderTop: '1px solid rgba(167, 139, 250, 0.3)',
                  marginBottom: '20px',
                  paddingTop: '20px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#A78BFA',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: '16px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>
                    📍 Location & Storage
                  </div>
                </div>

                {/* Location */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    color: '#E0E6F0',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>Location</label>
                  <select
                    value={bulkEditLocation}
                    onChange={(e) => setBulkEditLocation(e.target.value)}
                    style={{
                      backgroundColor: '#334155',
                      border: '2px solid #8A95AA',
                      borderRadius: '8px',
                      color: '#E0E6F0',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="">Don't change</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                {/* Tote or Shelf/Tier Choice */}
                {bulkEditLocation && (
                  <>
                    {/* Tote */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        color: '#E0E6F0',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'block',
                        marginBottom: '8px',
                        fontFamily: 'Rajdhani, sans-serif'
                      }}>Tote <span style={{ color: '#8A95AA', fontSize: '0.75rem' }}>(optional)</span></label>
                      <select
                        value={bulkEditTote}
                        onChange={(e) => {
                          setBulkEditTote(e.target.value)
                          // Clear shelf/tier if tote is selected
                          if (e.target.value) {
                            setBulkEditShelf('')
                            setBulkEditTier('')
                          }
                        }}
                        disabled={!!bulkEditShelf}
                        style={{
                          backgroundColor: bulkEditShelf ? '#1e293b' : '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: bulkEditShelf ? '#64748b' : '#E0E6F0',
                          padding: '0.75rem 1rem',
                          width: '100%',
                          fontSize: '0.875rem',
                          cursor: bulkEditShelf ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Don't change</option>
                        {bulkEditFilteredTotes.map(tote => (
                          <option key={tote.id} value={tote.id}>{tote.name}</option>
                        ))}
                      </select>
                      {bulkEditShelf && (
                        <div style={{ fontSize: '0.75rem', color: '#8A95AA', marginTop: '4px' }}>
                          Disabled because shelf is selected
                        </div>
                      )}
                    </div>

                    {/* Shelf */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{
                        color: '#E0E6F0',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'block',
                        marginBottom: '8px',
                        fontFamily: 'Rajdhani, sans-serif'
                      }}>Shelf <span style={{ color: '#8A95AA', fontSize: '0.75rem' }}>(optional)</span></label>
                      <select
                        value={bulkEditShelf}
                        onChange={(e) => {
                          setBulkEditShelf(e.target.value)
                          // Clear tote if shelf is selected
                          if (e.target.value) {
                            setBulkEditTote('')
                          }
                        }}
                        disabled={!!bulkEditTote}
                        style={{
                          backgroundColor: bulkEditTote ? '#1e293b' : '#334155',
                          border: '2px solid #8A95AA',
                          borderRadius: '8px',
                          color: bulkEditTote ? '#64748b' : '#E0E6F0',
                          padding: '0.75rem 1rem',
                          width: '100%',
                          fontSize: '0.875rem',
                          cursor: bulkEditTote ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Don't change</option>
                        {bulkEditFilteredShelves.map(shelf => (
                          <option key={shelf.id} value={shelf.id}>{shelf.name}</option>
                        ))}
                      </select>
                      {bulkEditTote && (
                        <div style={{ fontSize: '0.75rem', color: '#8A95AA', marginTop: '4px' }}>
                          Disabled because tote is selected
                        </div>
                      )}
                    </div>

                    {/* Tier */}
                    {bulkEditShelf && bulkEditFilteredTiers.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{
                          color: '#E0E6F0',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          display: 'block',
                          marginBottom: '8px',
                          fontFamily: 'Rajdhani, sans-serif'
                        }}>Tier <span style={{ color: '#8A95AA', fontSize: '0.75rem' }}>(optional)</span></label>
                        <select
                          value={bulkEditTier}
                          onChange={(e) => setBulkEditTier(e.target.value)}
                          style={{
                            backgroundColor: '#334155',
                            border: '2px solid #8A95AA',
                            borderRadius: '8px',
                            color: '#E0E6F0',
                            padding: '0.75rem 1rem',
                            width: '100%',
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="">Don't change</option>
                          {bulkEditFilteredTiers.map(tier => (
                            <option key={tier.id} value={tier.id}>
                              {tier.nickname || `Tier ${tier.position}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '32px', position: 'relative', zIndex: 10 }}>
                <button
                  onClick={async () => {
                    // Build update payload with only filled fields
                    const updates: any = {}
                    if (bulkEditCategory) updates.category_id = bulkEditCategory
                    if (bulkEditStatus) updates.status = bulkEditStatus
                    if (bulkEditCondition) updates.condition = bulkEditCondition
                    if (bulkEditQuantity) updates.quantity = parseInt(bulkEditQuantity)
                    if (bulkEditLocation) updates.location_id = bulkEditLocation
                    if (bulkEditShelf) updates.shelf_id = bulkEditShelf
                    if (bulkEditTier) updates.tier_id = bulkEditTier
                    if (bulkEditTote) updates.tote_id = bulkEditTote

                    if (Object.keys(updates).length === 0) {
                      toast({ title: 'No changes', description: 'Please select at least one field to update', type: 'error' })
                      return
                    }

                    try {
                      const itemIds = Array.from(selectedItems)
                      const response = await fetch('/api/central-optimized?op=bulk-edit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ item_ids: itemIds, updates })
                      })

                      const data = await response.json()
                      if (data.success) {
                        toast({ title: 'Success', description: `Updated ${itemIds.length} items`, type: 'success' })
                        setBulkEditSlideout(false)
                        setBulkEditCategory('')
                        setBulkEditStatus('')
                        setBulkEditCondition('')
                        setBulkEditQuantity('')
                        setBulkEditLocation('')
                        setBulkEditShelf('')
                        setBulkEditTier('')
                        setBulkEditTote('')
                        setSelectedItems(new Set())
                        fetchItems()
                      } else {
                        toast({ title: 'Error', description: data.message || 'Failed to update items', type: 'error' })
                      }
                    } catch (error) {
                      console.error('Error in bulk edit:', error)
                      toast({ title: 'Error', description: 'Failed to update items', type: 'error' })
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    background: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)',
                    border: '2px solid #A78BFA',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(167, 139, 250, 0.3)'
                  }}
                >
                  ✓ Update Items
                </button>
                <button
                  onClick={() => {
                    setBulkEditSlideout(false)
                    setBulkEditCategory('')
                    setBulkEditStatus('')
                    setBulkEditCondition('')
                    setBulkEditQuantity('')
                    setBulkEditLocation('')
                    setBulkEditShelf('')
                    setBulkEditTier('')
                    setBulkEditTote('')
                  }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    background: '#242938',
                    border: '2px solid #8A95AA',
                    color: '#8A95AA',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    borderRadius: '8px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Pick List Slideout */}
      {pickListSlideout && (
        <>
          <div onClick={() => setPickListSlideout(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 999
          }} />
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '600px',
            height: '100vh',
            background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.98), rgba(251, 146, 60, 0.1), rgba(17, 24, 39, 0.98))',
            backdropFilter: 'blur(12px)',
            borderLeft: '1px solid rgba(251, 146, 60, 0.3)',
            boxShadow: '0 0 40px rgba(251, 146, 60, 0.3)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderBottom: '1px solid rgba(249, 115, 22, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                color: '#f97316',
                fontSize: '28px',
                fontWeight: 700,
                fontFamily: 'Rajdhani, sans-serif'
              }}>
                📋 Add to Pick List
              </h2>
              <button onClick={() => setPickListSlideout(false)} style={{
                background: 'transparent',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                color: '#ef4444',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '16px',
                fontWeight: 600
              }}>
                ✕ Close
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              {/* Items Section */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{
                  color: '#06b6d4',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}>
                  Items to Add ({pickListItems.length})
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '8px',
                  padding: '15px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {pickListItems.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
                      No items added yet
                    </div>
                  ) : (
                    pickListItems.map(itemId => {
                      const item = items.find(i => i.id === itemId)
                      if (!item) return null
                      return (
                        <div key={itemId} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          background: 'rgba(6, 182, 212, 0.1)',
                          borderRadius: '6px',
                          marginBottom: '8px'
                        }}>
                          <div>
                            <div style={{ color: '#f97316', fontWeight: 600, fontSize: '16px' }}>{item.ic}</div>
                            <div style={{ color: '#94a3b8', fontSize: '14px' }}>{item.title}</div>
                          </div>
                          <button onClick={() => removeItemFromPickList(itemId)} style={{
                            background: 'transparent',
                            border: '1px solid rgba(239, 68, 68, 0.5)',
                            color: '#ef4444',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600
                          }}>
                            Remove
                          </button>
                        </div>
                      )
                    })
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <input
                    type="text"
                    value={addPickListItemInput}
                    onChange={(e) => setAddPickListItemInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddItemToPickList()}
                    placeholder="Scan or enter IC to add more items..."
                    style={{
                      flex: 1,
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(6, 182, 212, 0.5)',
                      borderRadius: '6px',
                      padding: '10px 15px',
                      color: '#e2e8f0',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '16px'
                    }}
                  />
                  <button onClick={handleAddItemToPickList} style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    color: 'white',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}>
                    + Add
                  </button>
                </div>
              </div>

              {/* Pick Lists Section */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{
                  color: '#06b6d4',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}>
                  Select Pick List
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '8px',
                  padding: '15px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {pickLists.map(pl => (
                    <div
                      key={pl.id}
                      onClick={() => { setSelectedPickList(pl.id); setCreateNewPickList(false); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        background: selectedPickList === pl.id ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.1)',
                        border: `1px solid ${selectedPickList === pl.id ? '#06b6d4' : 'rgba(6, 182, 212, 0.3)'}`,
                        borderRadius: '6px',
                        marginBottom: '10px',
                        cursor: 'pointer'
                      }}
                    >
                      <input
                        type="radio"
                        name="picklist"
                        checked={selectedPickList === pl.id}
                        onChange={() => {}}
                        style={{ marginRight: '12px', width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#f97316', fontWeight: 600, fontSize: '16px' }}>{pl.name}</div>
                        <div style={{ color: '#94a3b8', fontSize: '14px' }}>
                          {pl.item_count || 0} items • Created {new Date(pl.created_at).toLocaleString()}
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: 'rgba(34, 197, 94, 0.3)',
                        color: '#22c55e'
                      }}>
                        OPEN
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                  <input
                    type="checkbox"
                    id="createNewPickList"
                    checked={createNewPickList}
                    onChange={handleCreateNewToggle}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label htmlFor="createNewPickList" style={{
                    color: '#06b6d4',
                    fontWeight: 600,
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>
                    Create New Pick List
                  </label>
                </div>
                {createNewPickList && (
                  <input
                    type="text"
                    value={newPickListName}
                    onChange={(e) => setNewPickListName(e.target.value)}
                    placeholder="Enter pick list name..."
                    style={{
                      flex: 1,
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(6, 182, 212, 0.5)',
                      borderRadius: '6px',
                      padding: '10px 15px',
                      color: '#e2e8f0',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '16px',
                      marginTop: '10px',
                      width: '100%'
                    }}
                  />
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderTop: '1px solid rgba(249, 115, 22, 0.3)',
              display: 'flex',
              gap: '15px'
            }}>
              <button onClick={() => handlePickListSubmit(true)} style={{
                flex: 1,
                padding: '14px 24px',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '18px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: 'white',
                boxShadow: '0 4px 6px rgba(34, 197, 94, 0.3)'
              }}>
                ▶ Start Picking
              </button>
              <button onClick={() => handlePickListSubmit(false)} style={{
                flex: 1,
                padding: '14px 24px',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '18px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white',
                boxShadow: '0 4px 6px rgba(6, 182, 212, 0.3)'
              }}>
                💾 Save
              </button>
              <button onClick={() => setPickListSlideout(false)} style={{
                flex: 1,
                padding: '14px 24px',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '18px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                boxShadow: '0 4px 6px rgba(100, 116, 139, 0.3)'
              }}>
                ✕ Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* Sold Confirmation Slideout */}
      {soldSlideout && (
        <>
          <div onClick={() => setSoldSlideout(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 999
          }} />
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '600px',
            height: '100vh',
            background: 'linear-gradient(to bottom right, rgba(17, 24, 39, 0.98), rgba(251, 146, 60, 0.1), rgba(17, 24, 39, 0.98))',
            backdropFilter: 'blur(12px)',
            borderLeft: '1px solid rgba(251, 146, 60, 0.3)',
            boxShadow: '0 0 40px rgba(251, 146, 60, 0.3)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderBottom: '1px solid rgba(249, 115, 22, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                color: '#f97316',
                fontSize: '28px',
                fontWeight: 700,
                fontFamily: 'Rajdhani, sans-serif'
              }}>
                💰 Confirm Items Sold
              </h2>
              <button onClick={() => setSoldSlideout(false)} style={{
                background: 'transparent',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                color: '#ef4444',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '16px',
                fontWeight: 600
              }}>
                ✕ Close
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              {/* Items Section */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{
                  color: '#06b6d4',
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: 'Rajdhani, sans-serif'
                }}>
                  Items to Mark as Sold ({soldItems.length})
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(6, 182, 212, 0.3)',
                  borderRadius: '8px',
                  padding: '15px',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  {soldItems.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
                      No items added yet
                    </div>
                  ) : (
                    soldItems.map(itemId => {
                      const item = items.find(i => i.id === itemId)
                      if (!item) return null
                      return (
                        <div key={itemId} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          background: 'rgba(6, 182, 212, 0.1)',
                          borderRadius: '6px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#f97316', fontWeight: 600, fontSize: '16px' }}>{item.ic}</div>
                            <div style={{ color: '#94a3b8', fontSize: '14px' }}>{item.title}</div>
                            <div style={{ color: '#64748b', fontSize: '13px' }}>
                              Qty: {item.quantity} | {item.condition} | {item.location_name} - {item.tote_code || 'N/A'}
                            </div>
                          </div>
                          <button onClick={() => removeItemFromSold(itemId)} style={{
                            background: 'transparent',
                            border: '1px solid rgba(239, 68, 68, 0.5)',
                            color: '#ef4444',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            fontWeight: 600
                          }}>
                            Remove
                          </button>
                        </div>
                      )
                    })
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <input
                    type="text"
                    value={addSoldItemInput}
                    onChange={(e) => setAddSoldItemInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddItemToSold()}
                    placeholder="Scan or enter IC to add more items..."
                    style={{
                      flex: 1,
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(6, 182, 212, 0.5)',
                      borderRadius: '6px',
                      padding: '10px 15px',
                      color: '#e2e8f0',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '16px'
                    }}
                  />
                  <button onClick={handleAddItemToSold} style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    color: 'white',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}>
                    + Add
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderTop: '1px solid rgba(249, 115, 22, 0.3)',
              display: 'flex',
              gap: '15px'
            }}>
              <button onClick={handleProceedToSaleEntry} style={{
                flex: 1,
                padding: '14px 24px',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '18px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: 'white',
                boxShadow: '0 4px 6px rgba(34, 197, 94, 0.3)'
              }}>
                ▶ Proceed to Sale Entry
              </button>
              <button onClick={() => setSoldSlideout(false)} style={{
                flex: 1,
                padding: '14px 24px',
                border: 'none',
                borderRadius: '6px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '18px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                boxShadow: '0 4px 6px rgba(100, 116, 139, 0.3)'
              }}>
                ✕ Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        itemIds={Array.from(selectedItems).map(id => items.find(item => item.id === id)?.ic || '').filter(Boolean)}
        itemCount={selectedItems.size}
      />

      {/* Bulk Print Dialog */}
      <BulkPrintDialog
        isOpen={showBulkPrintDialog}
        onClose={() => setShowBulkPrintDialog(false)}
        items={Array.from(selectedItems).map(id => items.find(item => item.id === id)).filter(Boolean)}
        onPrintComplete={() => {
          setSelectedItems(new Set())
        }}
      />
    </div>
  )
}
